import React, {useRef, useState} from 'react'
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory, {textFilter, numberFilter, Comparator, dateFilter} from 'react-bootstrap-table2-filter';
import ToolkitProvider, {
    CSVExport,
    ColumnToggle
} from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min';
import paginationFactory from 'react-bootstrap-table2-paginator';
import {Button} from "react-bootstrap";
import Moment from "moment";
import {ExportCSV} from './ExportCSV';

const {ToggleList} = ColumnToggle;

const columns = [{
    dataField: 'ordinal',
    text: '#',
    sort: true
}, {
    dataField: 'uid',
    text: 'Uid',
    filter: textFilter()
}, {
    dataField: 'name',
    text: 'Name',
    sort: true,
    filter: textFilter()
}, {
    dataField: 'gender',
    text: 'Gender'
}, {
    dataField: 'birthday',
    text: 'Birthday'
}, {
    dataField: 'value',
    text: 'Value',
    sort: true,
    filter: numberFilter({
        defaultValue: {number: 0, comparator: Comparator.GT}
    })
}, {
    dataField: 'visitsCount',
    text: '# Visits',
    sort: true,
    filter: numberFilter({
        defaultValue: {number: 0, comparator: Comparator.GT}
    })
}, {
    dataField: 'listOfCities',
    text: 'List of Cities',
    filter: textFilter(),
    style: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
        maxWidth: '150px'
    },
    formatter: listFormatter
}, {
    dataField: 'cityList',
    text: 'City (Profile)',
    filter: textFilter(),
    style: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
        maxWidth: '150px'
    },
    formatter: listFormatter
}, {
    dataField: 'listOfStoresId',
    text: 'List of Stores Id',
    filter: textFilter(),
    style: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'normal',
        maxWidth: '200px'
    },
    formatter: listFormatter
}, {
    dataField: 'date',
    text: 'Date'
}, {
    dataField: 'time',
    text: 'Time'
}, {
    dataField: 'lastVisit',
    text: 'Last Visit',
    filter: dateFilter(),
    formatter: dateFormatter,
    csvFormatter: dateFormatter,
    hidden: false,
    sort: true
}, {
    dataField: 'daysSinceLastVisit',
    text: 'Days Since Visit',
    sort: true
}, {
    dataField: 'lastSeen',
    text: 'Last Seen',
    filter: dateFilter(),
    formatter: dateFormatter,
    csvFormatter: dateFormatter,
    hidden: false,
    sort: true
}, {
    dataField: 'daysSinceLastSeen',
    text: 'Days Since Seen',
    sort: true
}];

function dateFormatter(cell, row) {
    if (!cell) return '';
    return Moment.tz(cell, 'Europe/Belgrade').format('DD.MM.YYYY');
}

function listFormatter(cell, row) {
    if (!cell) return '';
    const items = cell.split(',').map(item => item.trim()).filter(item => item);
    return items.join('\n');
}

const SegmentTable = ({data, handleNewCampaignListUid, segmentName, company}) => {
    let bootstrapTableContainer = useRef(null);

    const options = {
        sizePerPageList: [
            {text: '50', value: 50},
            {text: '100', value: 100},
            {text: '250', value: 250},
            {text: 'All', value: data.length}
        ],
        showTotal: true
    };

    const handleNewCampaign = () => {
        const currentTableData = bootstrapTableContainer.filterContext.data;
        if (!currentTableData.length) {
            return;
        }
        const listOfFilteredUidFromTable = currentTableData.map(row => row.uid);
        handleNewCampaignListUid(listOfFilteredUidFromTable);
    };

    const exportData = data.map(row => ({
        uid: row.uid,
        name: row.name,
        gender: row.gender,
        birthday: row.birthday,
        value: row.value,
        visitsCount: row.visitsCount,
        listOfCities: row.listOfCities,
        listOfStoresId: row.listOfStoresId,
        cityList: row.cityList,
        date: row.date,
        time: row.time,
        daysSinceLastVisit: row.daysSinceLastVisit,
        lastSeen: row.lastSeen ? Moment(row.lastSeen).format('DD.MM.YYYY') : '',
        daysSinceLastSeen: row.daysSinceLastSeen
    }));

    return (
        <ToolkitProvider
            keyField='uid'
            data={data}
            columns={columns}
            columnToggle
            pagination={paginationFactory(options)}
            exportCSV={{
                exportAll: false,
                onlyExportFiltered: true
            }}>
            {props => (
                <div className="table-card">
                    {/* Segment Info */}
                    <div className="p-4 mb-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="text-lg font-semibold text-blue-800">{segmentName}</span>
                                <span className="ml-3 text-gray-600">Ukupno: <strong>{data.length}</strong> kupaca</span>
                            </div>
                        </div>
                    </div>

                    {/* Table Actions */}
                    <div className="table-actions">
                        <div className="flex flex-wrap items-center gap-3">
                            <ExportCSV
                                csvData={exportData}
                                fileName={company != null ? `${segmentName}_${company.name}` : `${segmentName}_Export`}
                            />
                            <Button variant="primary" onClick={handleNewCampaign}>
                                Nova Kampanja Za Ovaj Segment
                            </Button>
                        </div>
                    </div>

                    {/* Column Toggle */}
                    <div className="column-toggle-section">
                        <p className="column-toggle-label">Show/Hide Columns:</p>
                        <ToggleList {...props.columnToggleProps} />
                    </div>

                    {/* Table */}
                    <div className="table-responsive">
                        <BootstrapTable
                            striped
                            bordered
                            hover
                            {...props.baseProps}
                            ref={node => bootstrapTableContainer = node}
                            pagination={paginationFactory(options)}
                            bootstrap4={true}
                            tabIndexCell
                            filter={filterFactory()}
                        />
                    </div>
                </div>
            )}
        </ToolkitProvider>
    );
};

export const Segments = ({visits, handleNewCampaignListUid, company}) => {
    const [selectedSegmentTab, setSelectedSegmentTab] = useState(0);
    const [periodDays, setPeriodDays] = useState(90);

    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const ninetyDaysAgo = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);
    // Default date for users without lastSeen (Unix timestamp 0)
    const noSeenDate = new Date(0);

    // Transform visits to table data with daysSinceLastVisit and daysSinceLastSeen
    const transformVisit = (visit, index) => {
        const lastVisitDate = visit.timestamp;
        const daysSinceLastVisit = lastVisitDate
            ? Math.floor((now - lastVisitDate) / (1000 * 60 * 60 * 24))
            : null;

        // Handle lastSeen - use noSeenDate (Unix 0) if not present
        const hasLastSeen = !!visit.lastSeen;
        const lastSeenDate = hasLastSeen ?
            (visit.lastSeen.toDate ? visit.lastSeen.toDate() : new Date(visit.lastSeen))
            : noSeenDate;
        const daysSinceLastSeen = hasLastSeen
            ? Math.floor((now - lastSeenDate) / (1000 * 60 * 60 * 24))
            : null;

        return {
            ordinal: index + 1,
            uid: visit.uid,
            name: visit.firstName,
            gender: visit.gender,
            birthday: visit.birthday,
            value: visit.value,
            visitsCount: visit.count,
            listOfCities: visit.listOfCities,
            listOfStoresId: visit.listOfStoresId,
            cityList: visit.cityList,
            date: visit.date,
            time: visit.time,
            lastVisit: visit.timestamp,
            daysSinceLastVisit,
            lastSeen: lastSeenDate,
            daysSinceLastSeen
        };
    };

    // Segment filters
    const noviKupci = visits
        .filter(visit => visit.count === 1)
        .map((visit, index) => transformVisit(visit, index));

    const vracajuciKupci = visits
        .filter(visit => visit.count >= 2 && visit.count <= 4)
        .map((visit, index) => transformVisit(visit, index));

    const lojalniKupci = visits
        .filter(visit => visit.count >= 5)
        .map((visit, index) => transformVisit(visit, index));

    const neaktivniKupci = visits
        .filter(visit => {
            if (!visit.timestamp) return false;
            return visit.timestamp < thirtyDaysAgo;
        })
        .map((visit, index) => transformVisit(visit, index));

    // Engagement-based segments (based on lastSeen and lastVisit in last 90 days)
    const getLastSeenDate = (visit) => {
        if (!visit.lastSeen) return noSeenDate;
        return visit.lastSeen.toDate ? visit.lastSeen.toDate() : new Date(visit.lastSeen);
    };

    // Gledaju i kupuju - have both lastSeen AND lastVisit in last 90 days
    const gledajuIKupuju = visits
        .filter(visit => {
            const lastSeenDate = getLastSeenDate(visit);
            const lastVisitDate = visit.timestamp;
            return lastSeenDate >= ninetyDaysAgo && lastVisitDate && lastVisitDate >= ninetyDaysAgo;
        })
        .map((visit, index) => transformVisit(visit, index));

    // Samo gledaju - have lastSeen in last 90 days but NO lastVisit in last 90 days
    const samoGledaju = visits
        .filter(visit => {
            const lastSeenDate = getLastSeenDate(visit);
            const lastVisitDate = visit.timestamp;
            const hasRecentSeen = lastSeenDate >= ninetyDaysAgo;
            const hasRecentVisit = lastVisitDate && lastVisitDate >= ninetyDaysAgo;
            return hasRecentSeen && !hasRecentVisit;
        })
        .map((visit, index) => transformVisit(visit, index));

    // Samo kupuju - have lastVisit in last 90 days but NO lastSeen in last 90 days
    const samoKupuju = visits
        .filter(visit => {
            const lastSeenDate = getLastSeenDate(visit);
            const lastVisitDate = visit.timestamp;
            const hasRecentSeen = lastSeenDate >= ninetyDaysAgo;
            const hasRecentVisit = lastVisitDate && lastVisitDate >= ninetyDaysAgo;
            return hasRecentVisit && !hasRecentSeen;
        })
        .map((visit, index) => transformVisit(visit, index));

    const segments = [
        { name: 'Novi kupci', data: noviKupci, description: '1 poseta', color: 'green' },
        { name: 'Vraćajući kupci', data: vracajuciKupci, description: '2-4 posete', color: 'blue' },
        { name: 'Lojalni', data: lojalniKupci, description: '5+ poseta', color: 'purple' },
        { name: 'Neaktivni', data: neaktivniKupci, description: '30+ dana bez posete', color: 'orange' },
        { name: 'Gledaju i kupuju', data: gledajuIKupuju, description: 'Aktivni (90 dana)', color: 'emerald' },
        { name: 'Samo gledaju', data: samoGledaju, description: 'Potencijal za konverziju', color: 'sky' },
        { name: 'Samo kupuju', data: samoKupuju, description: 'Ne prate notifikacije', color: 'amber' }
    ];

    return (
        <div>
            {/* Period Selector */}
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px'}}>
                <label style={{fontSize: '14px', fontWeight: '500', color: '#374151'}}>Period za segmente angažmana:</label>
                <input
                    type="number"
                    min="1"
                    max="365"
                    value={periodDays}
                    onChange={e => setPeriodDays(Number(e.target.value))}
                    style={{width: '80px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px'}}
                />
                <span style={{fontSize: '14px', color: '#6b7280'}}>dana</span>
            </div>

            {/* Purchase-based Segments */}
            <p style={{marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#6b7280'}}>Segmenti po kupovinama:</p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px'}}>
                {segments.slice(0, 4).map((segment, index) => (
                    <div
                        key={segment.name}
                        style={{
                            padding: '16px',
                            borderRadius: '8px',
                            border: selectedSegmentTab === index ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                            backgroundColor: selectedSegmentTab === index ? '#eff6ff' : '#fff',
                            cursor: 'pointer',
                            textAlign: 'center'
                        }}
                        onClick={() => setSelectedSegmentTab(index)}
                    >
                        <div style={{fontSize: '24px', fontWeight: 'bold', color: '#111827'}}>{segment.data.length}</div>
                        <div style={{fontSize: '14px', fontWeight: '500', color: '#374151'}}>{segment.name}</div>
                        <div style={{fontSize: '12px', color: '#6b7280'}}>{segment.description}</div>
                    </div>
                ))}
            </div>

            {/* Engagement-based Segments */}
            <p style={{marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#6b7280'}}>Segmenti po angažmanu (poslednjih {periodDays} dana):</p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px'}}>
                {segments.slice(4).map((segment, index) => {
                    const isSelected = selectedSegmentTab === index + 4;
                    const colors = {
                        'Gledaju i kupuju': { border: '#10b981', bg: '#ecfdf5', text: '#059669' },
                        'Samo gledaju': { border: '#3b82f6', bg: '#eff6ff', text: '#2563eb' },
                        'Samo kupuju': { border: '#f59e0b', bg: '#fffbeb', text: '#d97706' }
                    };
                    const color = colors[segment.name] || { border: '#e5e7eb', bg: '#fff', text: '#111827' };

                    return (
                        <div
                            key={segment.name}
                            style={{
                                padding: '16px',
                                borderRadius: '8px',
                                border: isSelected ? `2px solid ${color.border}` : '2px solid #e5e7eb',
                                backgroundColor: isSelected ? color.bg : '#fff',
                                cursor: 'pointer',
                                textAlign: 'center'
                            }}
                            onClick={() => setSelectedSegmentTab(index + 4)}
                        >
                            <div style={{fontSize: '24px', fontWeight: 'bold', color: color.text}}>{segment.data.length}</div>
                            <div style={{fontSize: '14px', fontWeight: '500', color: '#374151'}}>{segment.name}</div>
                            <div style={{fontSize: '12px', color: '#6b7280'}}>{segment.description}</div>
                        </div>
                    );
                })}
            </div>

            {/* Segment Tabs */}
            <Tabs selectedIndex={selectedSegmentTab} onSelect={index => setSelectedSegmentTab(index)}>
                <TabList>
                    <Tab>Novi kupci ({noviKupci.length})</Tab>
                    <Tab>Vraćajući kupci ({vracajuciKupci.length})</Tab>
                    <Tab>Lojalni ({lojalniKupci.length})</Tab>
                    <Tab>Neaktivni ({neaktivniKupci.length})</Tab>
                    <Tab>Gledaju i kupuju ({gledajuIKupuju.length})</Tab>
                    <Tab>Samo gledaju ({samoGledaju.length})</Tab>
                    <Tab>Samo kupuju ({samoKupuju.length})</Tab>
                </TabList>

                <TabPanel>
                    <SegmentTable
                        data={noviKupci}
                        handleNewCampaignListUid={handleNewCampaignListUid}
                        segmentName="Novi kupci"
                        company={company}
                    />
                </TabPanel>

                <TabPanel>
                    <SegmentTable
                        data={vracajuciKupci}
                        handleNewCampaignListUid={handleNewCampaignListUid}
                        segmentName="Vraćajući kupci"
                        company={company}
                    />
                </TabPanel>

                <TabPanel>
                    <SegmentTable
                        data={lojalniKupci}
                        handleNewCampaignListUid={handleNewCampaignListUid}
                        segmentName="Lojalni"
                        company={company}
                    />
                </TabPanel>

                <TabPanel>
                    <SegmentTable
                        data={neaktivniKupci}
                        handleNewCampaignListUid={handleNewCampaignListUid}
                        segmentName="Neaktivni"
                        company={company}
                    />
                </TabPanel>

                <TabPanel>
                    <SegmentTable
                        data={gledajuIKupuju}
                        handleNewCampaignListUid={handleNewCampaignListUid}
                        segmentName="Gledaju i kupuju"
                        company={company}
                    />
                </TabPanel>

                <TabPanel>
                    <SegmentTable
                        data={samoGledaju}
                        handleNewCampaignListUid={handleNewCampaignListUid}
                        segmentName="Samo gledaju"
                        company={company}
                    />
                </TabPanel>

                <TabPanel>
                    <SegmentTable
                        data={samoKupuju}
                        handleNewCampaignListUid={handleNewCampaignListUid}
                        segmentName="Samo kupuju"
                        company={company}
                    />
                </TabPanel>
            </Tabs>
        </div>
    );
};
